import { castArray, each, find, isNil, memoize, sortBy } from 'lodash-es';

import { SIDEBAR_ITEM_ORDER_NAMESPACE } from 'consts';
import icons from 'icons';
import { services } from 'services/base';
import {
  ApplicationModuleConfig,
  ModuleConfig,
  ModuleType,
  NavigationArea,
  PluginModuleConfig,
  RouteType,
  SidebarItem,
  WidgetSidebarItem,
  WithNavigationArea,
} from 'types';

import { getModulesConfig } from './config';
import { isAvailable } from './helpers';

export interface SidebarCustomizationProps {
  customizedOrder: number;
  visibility: boolean;
}

export type SidebarCustomizationMap = Record<string, SidebarCustomizationProps>;

export const isAppModule = (module: ModuleConfig): module is ApplicationModuleConfig =>
  isNil(module.type) || [ModuleType.Application, 'sidebar'].includes(module.type);

export const isPluginModule = (module: ModuleConfig): module is PluginModuleConfig => module.type === ModuleType.Plugin;

export const isMobileNavigationModule = ({ navigation }: WithNavigationArea) =>
  isNil(navigation) || castArray(navigation).includes(NavigationArea.Mobile);

export const isSidebarNavigationModule = ({ navigation }: WithNavigationArea) =>
  isNil(navigation) || castArray(navigation).includes(NavigationArea.Sidebar);

const generateKey = (module: ModuleConfig, idx: number | undefined) =>
  `${module.id || (module as WidgetSidebarItem).widget || '-'}#${idx}`;

type NormalizeModuleConfigParams<T extends ModuleConfig> = {
  mainModule: T;
  mainModuleIdx: number;
  subModule?: T;
  subModuleIdx?: number;
};

const normalizeModuleConfig = <T extends ModuleConfig>({
  mainModule,
  mainModuleIdx,
  subModule,
  subModuleIdx,
}: NormalizeModuleConfigParams<T>): T => {
  const module = subModule || mainModule;
  const key =
    subModule?.key ||
    module?.key ||
    `${generateKey(module, mainModuleIdx)}/${subModule ? generateKey(subModule, subModuleIdx) : '-'}`;

  if (isAppModule(module)) {
    return {
      ...module,
      key,
      id: module.id || key,
      routeType: module.routeType || RouteType.Route,
      Icon: icons[module.icon] || undefined,
    };
  }

  return {
    ...module,
    key,
    id: module.id || key,
  };
};

export const normalizeModules = <T extends ModuleConfig>(modules: T[]): T[] => {
  return modules.map((mainModule, mainModuleIdx) => {
    if (isAppModule(mainModule)) {
      const result: T = {
        ...(normalizeModuleConfig({ mainModule, mainModuleIdx }) as T),
        submenu: mainModule.submenu?.map((subModule, subModuleIdx) =>
          normalizeModuleConfig({ mainModule, mainModuleIdx, subModule: subModule as unknown as T, subModuleIdx }),
        ),
      };
      return result;
    }
    return normalizeModuleConfig({ mainModule, mainModuleIdx });
  });
};

export const calcAvailableModules = <T extends ModuleConfig>(modules: T[]): T[] => {
  const availableModules = normalizeModules(
    modules
      .filter((module) => isAvailable(module.available))
      .map((mainModule) => ({
        ...mainModule,
        submenu:
          (isAppModule(mainModule) && mainModule.submenu?.filter((module) => isAvailable(module.available))) ||
          undefined,
      })),
  );

  return availableModules;
};

export const findModule = <ModuleType extends ModuleConfig>(availableModules: ModuleType[], moduleKey: string) => {
  const result: {
    mainModule: ModuleType | null;
    module: ModuleType | null;
  } = {
    mainModule: null,
    module: null,
  };

  each(availableModules, (mainModule) => {
    if (mainModule.key === moduleKey) {
      result.mainModule = mainModule;
      result.module = mainModule;
      return false;
    }

    const { submenu } = mainModule as ApplicationModuleConfig;
    const subModule = submenu && find(submenu, (submenuItem) => submenuItem.key === moduleKey);
    if (subModule) {
      result.module = subModule as ModuleType;
      result.mainModule = mainModule;

      return false;
    }

    return undefined;
  });

  return result;
};

export const calcRoutedModules = (modules: SidebarItem[]) => {
  const orderedModules = sortBy(modules, ({ priority }) => !priority, 'order');

  const storedItemCustomization =
    (services.appFrame.userStorage.getItem(SIDEBAR_ITEM_ORDER_NAMESPACE) as SidebarCustomizationMap) ?? null;
  const result: SidebarItem[] = [];

  orderedModules.forEach((module) => {
    if (!isAppModule(module) || (module.routeType && module.routeType !== RouteType.Route)) {
      return;
    }

    const customizedItem = storedItemCustomization?.[module.id];
    if (!module.priority && customizedItem && !customizedItem.visibility) {
      return;
    }

    result.push(module);
    const submenu = module.submenu as SidebarItem[] | undefined;
    result.push(...(submenu?.filter((subModule) => !subModule.route.startsWith(module.route)) || []));
  });

  return result;
};
export const getAvailableModules = memoize(() => calcAvailableModules(getModulesConfig() || []));
